import { Box, Grid2, Tooltip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Fragment } from 'react/jsx-runtime';
import { ReactComponent as AddIcon } from '../../../../assets/icons/add-icon.svg';
import { ReactComponent as CreateScene3dIcon } from '../../../../assets/icons/create-3d-scene.svg';
import { ReactComponent as DownloadIcon } from '../../../../assets/icons/download-icon.svg';
import {
    MODELS_EXTENSIONS,
    MODELS_MAX_SIZE,
} from '../../../../constants/file.constants';
import { AttachmentStatus, Model3d } from '../../../../models/models';
import {
    createScene3dStart,
    uploadAttachmentStart,
} from '../../../../store/attachment/attachment.action';
import { openDialog } from '../../../../store/dialog/dialog.action';
import { DIALOG_TYPES } from '../../../../store/dialog/dialog.types';
import { setIsError } from '../../../../store/error/error.action';
import { getNodeStart } from '../../../../store/node/node.action';
import { selectNodeSelected } from '../../../../store/node/node.selector';
import {
    getModel3dStatusDate,
    getModel3dStatusUser,
    getStatusLabel,
} from '../../../../utils/data/data.utils';
import { canAddAttachment, isViewer } from '../../../../utils/role/role.utils';
import { Model3dIcon } from '../../../attachment-status-icon/attachment-status-icon.component';
import Model3dStatusIcon from '../../../attachment-status-icon/model-3d-status-icon.component';
import {
    CustomAccordionDetailsGridText,
    CustomAccordionDetailsTitleText,
} from '../node-accordion-details.styles';

const NodeAccordionDetailsModels3dGrid = () => {
    const nodeSelected = useSelector(selectNodeSelected);

    const dispatch = useDispatch();

    const handleOpenAttachmentActions = (
        id: number,
        status: AttachmentStatus,
        refusal_notes: string,
        created_by: string,
        approved_by: string,
        rejected_by: string,
        replaced_by: string
    ) => {
        dispatch(
            openDialog(
                DIALOG_TYPES.SELECT_ATTACHMENT_ACTION,
                {
                    id: id,
                    type: 'model3d',
                    status: status,
                    refusal_notes: refusal_notes,
                    created_by: created_by,
                    approved_by: approved_by,
                    rejected_by: rejected_by,
                    replaced_by: replaced_by,
                },
                'xs'
            )
        );
    };

    const handleFileChange = (event: any) => {
        const files = event.target.files;
        if (files.length > 0) {
            const file = files[0];
            if (file.size > MODELS_MAX_SIZE.value) {
                dispatch(
                    setIsError(
                        true,
                        'Il file è troppo grande, il limite massimo è  ' +
                            MODELS_MAX_SIZE.label
                    )
                );
                return;
            }
            const extension = file.name.split('.').pop();
            if (extension !== 'zip') {
                dispatch(
                    setIsError(
                        true,
                        `Estensioni supportate: ${Object.values(
                            MODELS_EXTENSIONS
                        ).join(', ')}`
                    )
                );
                return;
            }
            dispatch(
                uploadAttachmentStart(file, 'model3d', nodeSelected!.id, () => {
                    dispatch(getNodeStart(nodeSelected!.id));
                })
            );
            event.target.value = '';
        }
    };

    const handleCreateScene3dButtonClick = (b: Model3d) => {
        dispatch(
            createScene3dStart(b.id, nodeSelected!.id, () => {
                dispatch(getNodeStart(nodeSelected!.id));
            })
        );
    };

    return (
        <Fragment>
            <CustomAccordionDetailsTitleText>
                {`MODELLI 3D`}
                {canAddAttachment(nodeSelected!) && (
                    <Typography component={'label'} height={40}>
                        <AddIcon style={{ cursor: 'pointer' }} />
                        <input type="file" hidden onChange={handleFileChange} />
                    </Typography>
                )}
            </CustomAccordionDetailsTitleText>
            <Box my={2} />
            {nodeSelected &&
                nodeSelected.models_3d.map((b) => {
                    return (
                        <Grid2
                            container
                            height={40}
                            key={b.id}
                            alignItems={'center'}
                            onClick={() =>
                                !isViewer(nodeSelected!) &&
                                nodeSelected!.status.key === 'active' &&
                                handleOpenAttachmentActions(
                                    b.id,
                                    b.status.key,
                                    b.refusal_notes,
                                    b.created_by,
                                    b.approved_by,
                                    b.rejected_by,
                                    b.replaced_by
                                )
                            }
                            sx={{
                                '&:hover': {
                                    cursor:
                                        isViewer(nodeSelected!) ||
                                        nodeSelected!.status.key === 'in_review'
                                            ? 'default'
                                            : 'pointer',
                                    backgroundColor: (theme) =>
                                        isViewer(nodeSelected!) ||
                                        nodeSelected!.status.key === 'in_review'
                                            ? 'transparent'
                                            : theme.palette.action.hover,
                                },
                            }}
                        >
                            <Grid2 container size={4} gap={1}>
                                <Grid2
                                    size={1}
                                    container
                                    justifyContent={'end'}
                                    alignItems={'center'}
                                >
                                    <Model3dStatusIcon model3d={b} />
                                </Grid2>
                                <Grid2 size={7} container alignItems={'center'}>
                                    <Tooltip title={b.name}>
                                        <CustomAccordionDetailsGridText>
                                            {b.name}
                                        </CustomAccordionDetailsGridText>
                                    </Tooltip>
                                </Grid2>
                                <Grid2 container alignItems={'center'}>
                                    <Tooltip
                                        title={'Scarica'}
                                        onClick={(event) =>
                                            event.stopPropagation()
                                        }
                                    >
                                        <a
                                            href={b.path}
                                            download
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <DownloadIcon />
                                        </a>
                                    </Tooltip>
                                    <Box mx={0.5} />
                                    {nodeSelected.status.key !== 'in_review' &&
                                        nodeSelected.role !== 'viewer' && (
                                            <Tooltip title={'Crea scena 3D'}>
                                                <CreateScene3dIcon
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleCreateScene3dButtonClick(
                                                            b
                                                        );
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                </Grid2>
                            </Grid2>
                            <Grid2 size={3} container>
                                <Grid2 size={12} alignContent={'center'}>
                                    <CustomAccordionDetailsGridText>
                                        {getModel3dStatusDate(b)}
                                    </CustomAccordionDetailsGridText>
                                </Grid2>
                            </Grid2>
                            <Grid2 size={3} container alignItems={'center'}>
                                <CustomAccordionDetailsGridText>
                                    {getModel3dStatusUser(b)}
                                </CustomAccordionDetailsGridText>
                            </Grid2>
                            <Grid2 size={2} container alignItems={'center'}>
                                <Model3dIcon model3d={b} />
                                <Box mx={0.5} />
                                {(() => {
                                    const color = getStatusLabel(b);
                                    return (
                                        <CustomAccordionDetailsGridText
                                            color={color}
                                        >
                                            {b.status.key !== 'active'
                                                ? b.status.label[0].toUpperCase() +
                                                  b.status.label.slice(1)
                                                : ''}
                                        </CustomAccordionDetailsGridText>
                                    );
                                })()}
                            </Grid2>
                        </Grid2>
                    );
                })}
        </Fragment>
    );
};

export default NodeAccordionDetailsModels3dGrid;
